(function () {
  const U = window.UIX;
  const APP = window.APP;
  const el = U.el;
  const esc = U.esc;

  const TOP_OPTIONS = 5;
  const TRACE_TICKS = 62;
  const POLL_MS = 1000;
  const SPEEDS = [0.5, 1, 2, 4, 8, 16, 0];
  const FOLLOW_SLACK = 3;
  const FOLLOW_BOOST = 8;
  const PACE_CAP = 32;
  const SPEED_KEY = "riftbound-watch-speed";
  const FOLLOW_KEY = "riftbound-watch-follow";
  const PACE_KEY = "riftbound-watch-pace";
  const PACE_MS = 1000;
  const PACE_DECIDE_MS = 300;
  const PACE_MARGIN_MS = 60;
  const PACE_CHAIN_MS = 120;
  const PACE_FOLD_MAX = 6;
  const PLAN_MS = 1200;
  const DEFAULT_TAU = 0.25;
  const HALT_ARM_MS = 4000;

  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
  function pct(p) { return p === null || p === undefined ? "" : Math.round(p * 100) + "%"; }
  function shortLabel(s) { return String(s || "").replace(/ \((?:\d|free|from).*$/i, "").replace(/ - .*$/, "").replace(/^Choose: ?/, ""); }

  function crewOf(name) {
    const m = String(name || "").match(/^jev:([a-z-]+):(.+)$/i);
    if (m) return m[2].replace(/^.*\//, "") + " plans, Jev plays";
    if (/^jev(#b)?$/i.test(String(name || ""))) return "Jev plays, no planner";
    const direct = String(name || "").match(/^claude-play:([^#]+)/i);
    if (direct) return direct[1] + " plays every move";
    if (/^brain/i.test(String(name || ""))) return "Heuristic AI";
    if (/^native/i.test(String(name || ""))) return "Search player";
    return String(name || "");
  }

  function tidy(text) {
    return String(text || "").replace(/\*\*/g, "").split("\n")
      .map(function (l) { return l.replace(/^\s*#+\s*/, "").replace(/\s+/g, " ").trim(); })
      .filter(Boolean).join("\n").replace(/^(the )?plan( for turn \d+)?\s*:\s*/i, "");
  }

  function plannerOf(name) {
    const m = String(name || "").match(/^jev:[a-z-]+:(.+?)(#b)?$/i);
    return m ? m[1].replace(/^.*\//, "") : "The planner";
  }

  function index(w) {
    w.byAt = {};
    const planNow = ["", ""], metaNow = [null, null];
    for (const t of w.trace) {
      w.byAt[t.at] = t;
      if (t.tau) w.tau = t.tau;
      t.planBefore = planNow[t.seat];
      t.metaBefore = metaNow[t.seat];
      if (t.direct && t.plan) {
        planNow[t.seat] = tidy(t.plan);
        metaNow[t.seat] = { reason: "note", turn: t.turn };
      }
      for (const p of t.plans || []) {
        if (!p.text) continue;
        p.text = tidy(p.text);
        planNow[t.seat] = p.text;
        metaNow[t.seat] = { reason: p.reason, turn: t.turn };
      }
      t.planAfter = planNow[t.seat] || tidy(t.plan);
      t.metaAfter = metaNow[t.seat];
    }
  }

  function beatsFor(w, at) {
    const t = w.byAt[at];
    if (!t) return [{ type: "apply", ms: 260 }];
    const beats = [];
    for (const p of t.plans || []) {
      if (!p.text) continue;
      if (p.reason === "handback") beats.push({ type: "asked", entry: t, ms: 1500 });
      beats.push({ type: "writing", entry: t, reason: p.reason, secs: Math.max(1, Math.round((p.ms || 0) / 1000)), ms: clamp((p.ms || 0) * 0.6, 1500, 2400) });
      beats.push({ type: "plan", entry: t, reason: p.reason, ms: PLAN_MS });
    }
    const quick = t.options.length <= 1 || (/^Pass\b/.test(t.played || "") && t.conf >= 0.8);
    beats.push({ type: "decide", entry: t, ms: quick ? 500 : 1400 + (t.conf < 0.4 ? 600 : 0) });
    beats.push({ type: "apply", entry: t, ms: 0 });
    return beats;
  }

  function cursor() { return APP.game ? APP.game.log.length : 0; }

  function speedLabel(v) { return v === 0 ? "Max" : v + "x"; }

  function backlog(w) {
    if (!w.live) return 1;
    return clamp((w.log.length - cursor()) / FOLLOW_SLACK, 1, FOLLOW_BOOST);
  }

  function pace(w) {
    if (w.pace) return 1;
    if (w.speed === 0) return 0;
    if (!w.live || !w.follow) return w.speed;
    return Math.min(PACE_CAP, w.speed * backlog(w));
  }

  function syncPace(w) {
    const v = pace(w);
    if (RB.Anim && RB.Anim.speed !== v) {
      RB.Anim.speed = v;
      if (v === 0) RB.Anim.flush();
    }
    if (RB.Anim && !w.pace) RB.Anim.budget = 0;
    const mk = (w.pace ? "p" : "") + v;
    if (w.node && w.mk !== mk) {
      w.mk = mk;
      w.node.style.setProperty("--mk", w.pace ? "0.5" : v ? String(Math.min(1, 1 / v)) : "0");
    }
    return v;
  }

  function stopTimer(w) {
    if (w.timer) { clearTimeout(w.timer); w.timer = null; }
  }

  function applyNext(w) {
    const at = cursor();
    if (at >= w.log.length) return false;
    U.applyAction(w.log[at]);
    if (cursor() === at) {
      w.paused = true;
      w.error = APP.error || "the recorded action no longer applies";
      return false;
    }
    return true;
  }

  function land(w) {
    w.applying = true;
    const ok = applyNext(w);
    w.applying = false;
    return ok;
  }

  function later(w, ms) {
    w.timer = setTimeout(function () { w.timer = null; tick(); }, Math.max(0, Math.ceil(ms)));
  }

  function landPaced(w, traced) {
    let moved = false;
    for (let n = 0; n < PACE_FOLD_MAX; n++) {
      if (RB.Anim) RB.Anim.budget = Math.max(120, w.slotEnd - performance.now() - PACE_MARGIN_MS);
      if (!land(w)) break;
      if (RB.Anim && !RB.Anim.idle()) { moved = true; break; }
      if (cursor() >= w.log.length || w.byAt[cursor()]) break;
    }
    w.slotOpen = false;
    if (!traced && !moved) w.slotEnd = w.slotStart;
    refresh();
    tick();
  }

  function tickPaced(w, at) {
    const now = performance.now();
    if (w.beatAt !== at) { w.beats = beatsFor(w, at); w.beatAt = at; w.slotOpen = false; }
    const beat = w.beats[0];
    if (!beat) return;
    if (!w.slotOpen && w.slotEnd > now) { later(w, w.slotEnd - now); return; }
    if (beat.type !== "decide" && beat.type !== "apply") {
      w.beats.shift();
      w.slotEnd = 0;
      w.beat = beat;
      refresh();
      later(w, beat.ms / backlog(w));
      return;
    }
    if (!w.slotOpen) {
      w.slotOpen = true;
      w.slotStart = w.slotEnd && now - w.slotEnd < PACE_CHAIN_MS ? w.slotEnd : now;
      w.slotEnd = w.slotStart + PACE_MS;
    }
    const lead = w.slotStart + PACE_DECIDE_MS - now;
    if (beat.type === "apply" && lead > 0) { later(w, lead); return; }
    w.beats.shift();
    if (beat.type === "apply") { landPaced(w, !!beat.entry); return; }
    w.beat = beat;
    refresh();
    later(w, lead);
  }

  function tick() {
    const w = APP.watch;
    if (!w || w.paused || w.timer || w.applying || APP.screen !== "game" || !U.live()) return;
    if (RB.Anim && !RB.Anim.idle()) return;
    const at = cursor();
    if (at >= w.log.length) {
      if (!w.live && !w.done) { w.done = true; refresh(); }
      return;
    }
    const rate = syncPace(w);
    if (w.pace) { tickPaced(w, at); return; }
    if (w.beatAt !== at) { w.beats = beatsFor(w, at); w.beatAt = at; }
    if (!rate) w.beats = w.beats.filter(function (b) { return b.type === "apply"; });
    const beat = w.beats.shift();
    if (!beat) return;
    if (beat.type === "apply") {
      w.timer = setTimeout(function () {
        w.timer = null;
        land(w);
        refresh();
        tick();
      }, rate ? Math.round(beat.ms / rate) : 0);
      return;
    }
    w.beat = beat;
    refresh();
    later(w, beat.ms / rate);
  }

  function setPaused(v) {
    const w = APP.watch;
    if (!w) return;
    w.paused = !!v;
    if (w.paused) stopTimer(w);
    refresh();
    if (!w.paused) tick();
  }

  function step() {
    const w = APP.watch;
    if (!w) return;
    w.paused = true;
    stopTimer(w);
    if (RB.Anim) RB.Anim.flush();
    const t = w.byAt[cursor()];
    if (t) w.beat = { type: "decide", entry: t };
    w.beats = [];
    w.beatAt = -1;
    applyNext(w);
    refresh();
  }

  function seek(idx) {
    const w = APP.watch;
    if (!w) return;
    stopTimer(w);
    w.beats = [];
    w.beatAt = -1;
    w.beat = null;
    w.done = false;
    w.slotEnd = 0;
    w.slotOpen = false;
    U.rewindTo(w.log.slice(0, clamp(idx, 0, w.log.length)));
    U.render();
    refresh();
    if (!w.paused) tick();
  }

  function setSpeed(v) {
    const w = APP.watch;
    if (!w || SPEEDS.indexOf(v) < 0) return;
    w.speed = v;
    try { localStorage.setItem(SPEED_KEY, String(v)); } catch (e) { }
    syncPace(w);
    refresh();
    if (!w.paused) tick();
  }

  function nudgeSpeed(by) {
    const w = APP.watch;
    if (!w) return;
    setSpeed(SPEEDS[clamp(SPEEDS.indexOf(w.speed) + by, 0, SPEEDS.length - 1)]);
  }

  function setPace(v) {
    const w = APP.watch;
    if (!w) return;
    w.pace = !!v;
    w.slotEnd = 0;
    w.slotOpen = false;
    try { localStorage.setItem(PACE_KEY, w.pace ? "1" : "0"); } catch (e) { }
    syncPace(w);
    refresh();
    if (!w.paused) tick();
  }

  function setFollow(v) {
    const w = APP.watch;
    if (!w) return;
    w.follow = !!v;
    try { localStorage.setItem(FOLLOW_KEY, w.follow ? "1" : "0"); } catch (e) { }
    syncPace(w);
    refresh();
  }

  function endLive(w, why) {
    w.live = false;
    w.pending = null;
    w.ended = why || null;
    if (w.clock) { clearInterval(w.clock); w.clock = null; }
  }

  function feedLost(w) {
    endLive(w, "The live feed ended before the game did.");
    refresh();
    fetch("/api/arena/job").then(function (r) { return r.ok ? r.json() : null; }).then(function (st) {
      if (!st || APP.watch !== w || st.gameId !== w.id || st.running) return;
      const last = String(st.tail || "").trim().split("\n").pop();
      w.ended = "The arena stopped (" + st.exit + ")" + (last ? ": " + last : ".");
      refresh();
    }).catch(function () { });
  }

  function checkJob(w) {
    fetch("/api/arena/job").then(function (r) { return r.ok ? r.json() : null; }).then(function (st) {
      if (APP.watch !== w) return;
      w.stoppable = !!(st && st.running && st.gameId === w.id);
      refresh();
    }).catch(function () { });
  }

  function stopGame() {
    const w = APP.watch;
    if (!w || !w.live || !w.stoppable || w.halt === "sent") return;
    if (w.halt !== "armed") {
      w.halt = "armed";
      w.haltErr = null;
      w.disarm = setTimeout(function () { if (w.halt === "armed") { w.halt = null; refresh(); } }, HALT_ARM_MS);
      refresh();
      return;
    }
    clearTimeout(w.disarm);
    w.halt = "sent";
    refresh();
    fetch("/api/arena/job/stop", { method: "POST" }).then(function (r) { return r.json(); }).then(function (j) {
      if (j.error) { w.halt = null; w.haltErr = j.error; }
      refresh();
    }).catch(function () { w.halt = null; w.haltErr = "the server did not answer"; refresh(); });
  }

  function poll() {
    const w = APP.watch;
    if (!w || !w.live) return;
    fetch("/api/arena/replays/" + encodeURIComponent(w.id)).then(function (r) {
      if (r.status === 404) { if (APP.watch === w) feedLost(w); return null; }
      return r.ok ? r.json() : null;
    }).then(function (rec) {
      if (APP.watch !== w || !w.live) return;
      if (!rec) { setTimeout(poll, POLL_MS * 2); return; }
      if (rec.log.length > w.log.length) {
        w.log = rec.log;
        w.trace = rec.trace || [];
        index(w);
      }
      w.pending = rec.live ? rec.pending || null : null;
      if (!rec.live) { w.over = rec.over || null; endLive(w, null); }
      refresh();
      tick();
      if (w.live) setTimeout(poll, POLL_MS);
    }).catch(function () { if (APP.watch === w && w.live) setTimeout(poll, POLL_MS * 2); });
  }

  function start(rec, id) {
    const game = { version: rec.version, setup: rec.setup, log: [] };
    if (!U.loadGame(game, "That arena game")) { APP.screen = "watchlist"; U.render(); return; }
    let speed = 1, follow = true, paced = true;
    try {
      const raw = localStorage.getItem(SPEED_KEY);
      if (raw !== null && raw !== "" && SPEEDS.indexOf(+raw) >= 0) speed = +raw;
      follow = localStorage.getItem(FOLLOW_KEY) !== "0";
      paced = localStorage.getItem(PACE_KEY) !== "0";
    } catch (e) { }
    const w = {
      id: id, log: rec.log, trace: rec.trace || [], seats: (rec.seats || []).map(function (s) { return s.name; }),
      tau: rec.tau || DEFAULT_TAU, live: !!rec.live, over: rec.over || null, paused: false, speed: speed,
      beats: [], beatAt: -1, beat: null, timer: null, done: false, error: null, node: null, key: "",
      pending: rec.live ? rec.pending || null : null, clock: null, parts: null, mk: null,
      follow: follow, pace: paced, slotEnd: 0, slotStart: 0, slotOpen: false, applying: false, animSpeed: RB.Anim ? RB.Anim.speed : 1, stoppable: false, halt: null, haltErr: null, disarm: null, ended: null, scrubbing: false,
    };
    index(w);
    APP.watch = w;
    syncPace(w);
    APP.ai = [true, true];
    APP.viewAs = 0;
    APP.showReplay = false;
    APP.screen = "game";
    U.render();
    if (w.live) { setTimeout(poll, POLL_MS); w.clock = setInterval(refresh, 1000); checkJob(w); }
  }

  function watchStop() {
    const w = APP.watch;
    if (!w) return;
    stopTimer(w);
    if (w.clock) clearInterval(w.clock);
    clearTimeout(w.disarm);
    if (RB.Anim) { RB.Anim.speed = w.animSpeed; RB.Anim.budget = 0; }
    w.live = false;
    APP.watch = null;
    APP.ai = [false, false];
    APP.viewAs = "auto";
  }

  function livePending(w, seat) {
    if (!w.live || !w.pending || w.pending.seat !== seat || cursor() < w.log.length) return null;
    return w.pending;
  }

  function open(id) {
    fetch("/api/arena/replays/" + encodeURIComponent(id)).then(function (r) { return r.ok ? r.json() : null; }).then(function (rec) {
      if (!rec) { APP.error = "No arena game called " + id + "."; APP.screen = "watchlist"; loadList(); return; }
      start(rec, id);
    }).catch(function () { APP.error = "The arena feed did not answer."; APP.screen = "watchlist"; loadList(); });
  }

  let listing = null;

  function loadList() {
    U.render();
    fetch("/api/arena/replays").then(function (r) { return r.ok ? r.json() : []; }).then(function (rows) {
      listing = rows;
      if (APP.screen === "watchlist") U.render();
    }).catch(function () { listing = []; if (APP.screen === "watchlist") U.render(); });
  }

  function renderWatchList() {
    const root = document.getElementById("app");
    root.innerHTML = "";
    root.className = "watchlist";
    const box = el("div", "wlbox");
    box.appendChild(el("h1", null, "Arena games"));
    box.appendChild(el("p", "wlsub", "Games recorded by the arena with a decision trace. A game marked live is still being played."));
    if (APP.error) box.appendChild(el("p", "wlerr", esc(APP.error)));
    if (!listing) box.appendChild(el("p", "wlsub", "Loading..."));
    else if (!listing.length) box.appendChild(el("p", "wlsub", "Nothing recorded yet. Run the arena with --save-replays or --live."));
    else {
      const ul = el("ul", "wlrows");
      for (const g of listing) {
        const li = el("li");
        const a = el("a", null,
          "<span class='wlseats'>" + esc(g.seats.map(crewOf).join("  vs  ") || g.id) + "</span>" +
          "<span class='wlmeta'>" + (g.live ? "<b class='wllive'>LIVE</b> " : "") + esc(g.id) + " - " + g.actions + " actions, " +
          (g.traced ? g.traced + " traced decisions" : "no trace") +
          (g.over && g.over.winner !== null && g.over.winner !== undefined ? ", seat " + g.over.winner + " won" : "") + "</span>");
        a.href = "/watch?game=" + encodeURIComponent(g.id);
        li.appendChild(a);
        ul.appendChild(li);
      }
      box.appendChild(ul);
    }
    root.appendChild(box);
  }

  function watchBoot() {
    let id = null;
    try { id = new URLSearchParams(location.search).get("game"); } catch (e) { }
    document.addEventListener("keydown", function (ev) {
      if (!APP.watch || ev.target && /INPUT|TEXTAREA|SELECT/.test(ev.target.tagName)) return;
      if (ev.key === " ") { ev.preventDefault(); setPaused(!APP.watch.paused); }
      if (ev.key === "ArrowRight") { ev.preventDefault(); step(); }
      if (ev.key === "p" || ev.key === "P") { ev.preventDefault(); setPace(!APP.watch.pace); }
      if (ev.key === "+" || ev.key === "=") { ev.preventDefault(); nudgeSpeed(1); }
      if (ev.key === "-" || ev.key === "_") { ev.preventDefault(); nudgeSpeed(-1); }
    });
    APP.screen = "watchlist";
    if (id) { U.render(); open(id); } else loadList();
  }

  function seatView(w, seat) {
    const at = cursor();
    const beat = w.beat && w.beat.entry && w.beat.entry.seat === seat ? w.beat : null;
    const past = w.trace.filter(function (t) { return t.seat === seat && t.at < at; });
    const live = beat && beat.entry.at >= at ? beat.entry : null;
    const last = past.length ? past[past.length - 1] : null;
    const fresh = live && (beat.type === "plan" || beat.type === "decide");
    const plan = live ? (fresh ? live.planAfter : live.planBefore) : last ? last.planAfter : "";
    const meta = live ? (fresh ? live.metaAfter : live.metaBefore) : last ? last.metaAfter : null;
    const ticks = [], asked = [];
    let turn = null;
    const shown = live ? past.concat([live]) : past;
    for (const t of shown) {
      if (t.conf === null || t.conf === undefined) continue;
      const hb = t.handback && (t.plans || []).some(function (p) { return p.reason === "handback"; }) ? t.handback : null;
      const decided = t !== live || beat.type === "decide";
      if (!decided && !hb) continue;
      if (turn !== null && t.turn !== turn) ticks.push(0);
      turn = t.turn;
      if (hb) { asked.push(ticks.length); ticks.push(Math.max(0.02, hb.conf)); }
      if (decided) ticks.push(Math.max(0.02, t.conf));
    }
    return { plan: plan, meta: meta, ticks: ticks, asked: asked, beat: live ? beat : null };
  }

  function deskView(w) {
    const at = cursor();
    const beat = w.beat && w.beat.entry && w.beat.entry.at >= at ? w.beat : null;
    if (beat) {
      const asked = beat.type === "asked" || ((beat.type === "writing" || beat.type === "plan") && beat.reason === "handback");
      return { entry: beat.entry, mode: asked ? "asked" : beat.type === "decide" ? "decide" : "open" };
    }
    let last = null;
    for (const t of w.trace) { if (t.at < at) last = t; }
    return last ? { entry: last, mode: "played" } : null;
  }

  function put(node, text) {
    if (node.textContent !== text) node.textContent = text;
  }

  function cls(node, name) {
    if (node.className !== name) node.className = name;
  }

  function buildMeter(label, cls0, notch) {
    const m = el("div", "meter " + cls0);
    const row = el("div", "row"), name = el("span", null, label), value = el("span", null, "-");
    row.appendChild(name);
    row.appendChild(value);
    m.appendChild(row);
    const track = el("div", "track"), fill = el("i");
    track.appendChild(fill);
    let mark = null;
    if (notch) { mark = el("u"); mark.title = "below this, Jev asks for a new plan"; track.appendChild(mark); }
    m.appendChild(track);
    return { node: m, base: "meter " + cls0, value: value, fill: fill, mark: mark };
  }

  function setMeter(m, value, extra) {
    cls(m.node, m.base + (extra ? " " + extra : ""));
    put(m.value, value === null || value === undefined ? "-" : value.toFixed(2));
    m.fill.style.width = ((value || 0) * 100) + "%";
  }

  function panelSeat(w) {
    const at = cursor();
    const beat = w.beat && w.beat.entry && w.beat.entry.at >= at ? w.beat : null;
    if (beat && beat.type !== "decide") return beat.entry.seat;
    const pend = w.live && w.pending && at >= w.log.length ? w.pending : null;
    if (pend && pend.type === "planning") return pend.seat;
    const turn = APP.state.turn;
    return turn && turn.player === 1 ? 1 : 0;
  }

  function buildSeat() {
    const box = el("section", "seat");
    const who = el("div", "who");
    const legend = el("span", "legend"), crew = el("span", "crew");
    who.appendChild(legend);
    who.appendChild(crew);
    box.appendChild(who);
    const plan = el("div", "plan");
    const thinking = el("div", "thinking");
    const thought = el("span");
    thinking.appendChild(el("i"));
    thinking.appendChild(thought);
    const steps = el("div", "steps");
    steps.tabIndex = 0;
    plan.appendChild(thinking);
    plan.appendChild(steps);
    box.appendChild(plan);
    const trace = el("div", "trace");
    trace.title = "Jev's confidence at each decision. Gold: it asked for a new plan.";
    box.appendChild(trace);
    const s = { box: box, legend: legend, crew: crew, plan: plan, thought: thought, steps: steps, trace: trace, planKey: null, ticksKey: "", scroll: 0 };
    steps.addEventListener("scroll", function () { s.scroll = steps.scrollTop; });
    return s;
  }

  function buildDesk(w) {
    const desk = el("section", "desk");
    const now = el("div", "now");
    const head = el("div", "ask");
    const whose = el("span", "whose"), q = el("span", "q"), kind = el("span", "kind");
    head.appendChild(whose);
    head.appendChild(q);
    head.appendChild(kind);
    now.appendChild(head);
    const ul = el("ul", "opts");
    const rows = [];
    for (let i = 0; i < TOP_OPTIONS; i++) {
      const li = el("li", "blank"), fill = el("i"), lab = el("span", "lab"), p = el("span", "pct");
      li.appendChild(fill);
      li.appendChild(lab);
      li.appendChild(p);
      ul.appendChild(li);
      rows.push({ li: li, fill: fill, lab: lab, pct: p });
    }
    const more = el("li", "more");
    ul.appendChild(more);
    now.appendChild(ul);
    const read = el("div", "read");
    const conf = buildMeter("Confidence", "", true);
    const off = buildMeter("Board off plan", "off", false);
    const unsure = el("div", "asked-note", "Too unsure to play. Waiting for the new plan.");
    read.appendChild(conf.node);
    read.appendChild(off.node);
    read.appendChild(unsure);
    now.appendChild(read);
    desk.appendChild(now);

    const tally = el("div", "tally");
    const figures = el("div", "figures");
    const figs = ["Turn", "Decisions by Jev", "Plans written", "Spent so far"].map(function (label) {
      const cell = el("div"), value = el("strong");
      cell.appendChild(el("span", null, label));
      cell.appendChild(value);
      figures.appendChild(cell);
      return value;
    });
    tally.appendChild(figures);
    const controls = el("div", "controls");
    const play = el("button", "wbtn playbtn");
    play.title = "Space";
    play.onclick = function () { setPaused(!w.paused); };
    controls.appendChild(play);
    const next = el("button", "wbtn", "Step");
    next.title = "Right arrow";
    next.onclick = step;
    controls.appendChild(next);
    const speeds = el("div", "speeds");
    const slower = el("button", "wbtn nudge", "-");
    slower.title = "Slower (-)";
    slower.setAttribute("aria-label", "Slower");
    slower.onclick = function () { nudgeSpeed(-1); };
    const speedNow = el("span", "speednow");
    speedNow.title = "One factor for the board animations and the decision beats. Max plays without animation.";
    const faster = el("button", "wbtn nudge", "+");
    faster.title = "Faster (+)";
    faster.setAttribute("aria-label", "Faster");
    faster.onclick = function () { nudgeSpeed(1); };
    speeds.appendChild(slower);
    speeds.appendChild(speedNow);
    speeds.appendChild(faster);
    controls.appendChild(speeds);
    tally.appendChild(controls);
    const range = document.createElement("input");
    range.type = "range";
    range.className = "scrub";
    range.min = 0;
    range.setAttribute("aria-label", "Position in the game");
    range.oninput = function () { w.scrubbing = true; };
    range.onchange = function () { w.scrubbing = false; seek(+range.value); };
    tally.appendChild(range);
    const modes = el("div", "modes");
    const paced = el("button", "wbtn", "Pace");
    paced.title = "One action a second, animations fitted to the beat (P)";
    paced.onclick = function () { setPace(!w.pace); };
    const follow = el("button", "wbtn follow", "Keep up");
    follow.title = "Speed up whenever the live game gets ahead of what you are watching";
    follow.onclick = function () { setFollow(!w.follow); };
    const halt = el("button", "wbtn halt");
    halt.onclick = stopGame;
    modes.appendChild(paced);
    modes.appendChild(follow);
    modes.appendChild(halt);
    tally.appendChild(modes);
    const note = el("div", "note");
    tally.appendChild(note);
    desk.appendChild(tally);
    return {
      desk: desk, now: now, whose: whose, q: q, kind: kind, opts: ul, rows: rows, more: more, read: read, conf: conf, off: off, at: null,
      figs: figs, play: play, paced: paced, follow: follow, slower: slower, faster: faster, speedNow: speedNow, range: range, note: note, halt: halt,
    };
  }

  function paintSeat(w, s) {
    const seat = panelSeat(w);
    const v = seatView(w, seat);
    const pending = v.beat ? null : livePending(w, seat);
    cls(s.box, "seat " + U.pcls(seat));
    put(s.legend, U.seatName(APP.state, seat));
    const choosing = !!(pending && pending.type === "deciding");
    cls(s.crew, "crew" + (choosing ? " busy" : ""));
    put(s.crew, choosing ? "Jev is choosing..." : crewOf(w.seats[seat]));
    const drafting = pending && pending.type === "planning" ? pending : null;
    const writing = drafting || (v.beat && v.beat.type === "writing" ? v.beat : null);
    const silent = !w.trace.some(function (t) { return t.seat === seat; }) && !/^(jev|claude)/i.test(String(w.seats[seat] || ""));
    const text = v.plan || (silent ? "This side is not a language model and leaves no trace."
      : "No plan yet. The planner is asked at this seat's first turn with a real choice; a turn whose only legal action is to end it plays itself.");
    cls(s.plan, "plan" + (writing ? " busy" : ""));
    if (writing) {
      put(s.thought, (writing.reason === "handback" ? "Jev asked for a new plan. " : writing.reason === "checkpoint" ? "The plan said to look again here. " : "") + plannerOf(w.seats[seat]) + " is thinking" +
        (drafting ? ", " + Math.max(0, Math.round((Date.now() - drafting.since) / 1000)) + "s" : ""));
    }
    const planKey = seat + "|" + text;
    if (planKey !== s.planKey) {
      s.planKey = planKey;
      s.scroll = 0;
      const lines = text.split("\n");
      s.steps.textContent = "";
      lines.forEach(function (l) {
        const row = el("div", "stepline" + (/^opponent'?s turn\s*:/i.test(l) ? " after" : /^(think|budget|fight)\b[^:]{0,40}:/i.test(l) ? " aside" : ""));
        row.textContent = l;
        s.steps.appendChild(row);
      });
      s.steps.className = "steps" + (v.plan ? "" : " empty") + (lines.length > 1 ? " listed" : "");
      void s.steps.offsetWidth;
      s.steps.classList.add("fresh");
    }
    const ticksKey = seat + "|" + v.ticks.join(",") + "|" + v.asked.join(",") + "|" + !!v.beat;
    if (ticksKey !== s.ticksKey) {
      s.ticksKey = ticksKey;
      s.trace.textContent = "";
      const from = Math.max(0, v.ticks.length - TRACE_TICKS);
      v.ticks.forEach(function (c, i) {
        if (i < from) return;
        if (c === 0) { s.trace.appendChild(el("s")); return; }
        const b = el("b", (v.asked.indexOf(i) >= 0 ? "asked " : "") + (i === v.ticks.length - 1 && v.beat ? "cur" : ""));
        b.style.height = Math.round(4 + c * 20) + "px";
        b.title = "confidence " + c.toFixed(2);
        s.trace.appendChild(b);
      });
    }
  }

  function paintDesk(w, d) {
    const v = deskView(w);
    if (!v) {
      cls(d.now, "now vacant");
      put(d.whose, "");
      put(d.q, "No decision yet");
      put(d.kind, "");
      d.rows.forEach(function (r) { cls(r.li, "blank"); r.fill.style.width = "0%"; });
      d.at = null;
      put(d.more, "");
      cls(d.read, "read");
      setMeter(d.conf, null, "");
      setMeter(d.off, null, "");
      return;
    }
    const t = v.entry, asked = v.mode === "asked", open = v.mode === "open";
    cls(d.now, "now " + U.pcls(t.seat) + " " + v.mode + (t.direct ? " direct" : ""));
    put(d.whose, U.seatName(APP.state, t.seat));
    put(d.q, t.prompt || (t.kind === "respond" ? "React or pass?" : "Your move"));
    const kind = t.kind === "act" ? (t.mine ? "main phase" : "their turn") : t.kind === "respond" ? "reaction window" : t.kind;
    put(d.kind, v.mode === "played" ? "played" : kind);
    const first = asked && t.handback && t.handback.p ? t.handback.p : null;
    const rows = t.options.map(function (o, i) {
      return { label: shortLabel(o.label), p: open ? null : asked ? (first ? first[i] : null) : o.p, picked: !asked && !open && i === t.pick };
    });
    const pick = typeof t.pick === "number" ? t.pick : 0;
    const from = Math.min(Math.max(0, pick - TOP_OPTIONS + 1), Math.max(0, rows.length - TOP_OPTIONS));
    const shown = rows.slice(from, from + TOP_OPTIONS);
    if (d.at !== t.at) {
      d.at = t.at;
      cls(d.opts, "opts reset");
      d.rows.forEach(function (r) { r.fill.style.width = "0%"; });
      void d.opts.offsetWidth;
      cls(d.opts, "opts");
    }
    d.rows.forEach(function (r, i) {
      const o = shown[i];
      if (!o) { cls(r.li, "blank"); r.fill.style.width = "0%"; return; }
      cls(r.li, o.picked ? "picked" : "");
      put(r.lab, o.label);
      r.lab.title = o.label;
      put(r.pct, pct(o.p));
      r.fill.style.width = ((o.p || 0) * 100) + "%";
    });
    const rest = rows.filter(function (o, i) { return i < from || i >= from + TOP_OPTIONS; });
    const top = rest.reduce(function (m, o) { return Math.max(m, o.p || 0); }, 0);
    put(d.more, !rest.length ? "" : "and " + rest.length + " more" + (open || (asked && !first) ? "" : ", best " + Math.max(1, Math.round(top * 100)) + "%"));
    cls(d.read, "read" + (t.direct ? " mute" : "") + (asked ? " unsure" : ""));
    const conf = open ? null : asked && t.handback ? t.handback.conf : t.conf;
    setMeter(d.conf, conf, conf !== null && conf !== undefined && conf < w.tau ? "low" : "");
    d.conf.mark.style.left = (w.tau * 100) + "%";
    setMeter(d.off, asked || open ? null : t.diverge, "");
  }

  function paintTally(w, d) {
    const at = cursor();
    const seen = w.trace.filter(function (t) { return t.at < at; });
    const byJev = seen.filter(function (t) { return !t.direct; }).length;
    let plans = 0, cost = 0;
    for (const t of seen) {
      cost += (t.jev && t.jev.usage && t.jev.usage.cost) || 0;
      for (const p of t.plans || []) { if (p.text) plans++; cost += (p.usage && p.usage.cost) || 0; }
    }
    const state = APP.state;
    [state.turn.num, byJev, plans, "$" + cost.toFixed(cost < 0.1 ? 4 : 2)].forEach(function (x, i) { put(d.figs[i], String(x)); });
    put(d.play, w.paused ? "Play" : "Pause");
    const rate = pace(w);
    const boosted = !w.pace && rate > w.speed;
    put(d.speedNow, w.pace ? "1s" : boosted ? (Math.round(rate * 10) / 10) + "x" : speedLabel(w.speed));
    cls(d.speedNow, "speednow" + (boosted ? " boosted" : ""));
    d.slower.disabled = w.pace || SPEEDS.indexOf(w.speed) <= 0;
    d.faster.disabled = w.pace || SPEEDS.indexOf(w.speed) >= SPEEDS.length - 1;
    cls(d.paced, "wbtn" + (w.pace ? " on" : ""));
    cls(d.follow, "wbtn follow" + (w.follow && !w.pace ? " on" : "") + (w.live ? "" : " gone"));
    d.follow.disabled = w.pace;
    if (+d.range.max !== w.log.length) d.range.max = w.log.length;
    if (!w.scrubbing && +d.range.value !== at) d.range.value = at;

    let note = "";
    if (w.error) note = "Stopped: " + w.error;
    else if (w.halt === "sent" && w.live) note = "Stopping both players and the game...";
    else if (w.haltErr) note = "Could not stop the game: " + w.haltErr;
    else if (state.over) note = U.seatName(state, state.over.winner) + " wins " + state.players.map(function (p) { return p.points; }).sort(function (a, b) { return b - a; }).join(" - ") + ".";
    else if (w.live && at >= w.log.length) note = "Live. Waiting for the next move.";
    else if (w.live) note = "Live game, " + (w.log.length - at) + " actions behind" + (boosted ? ", catching up." : ".");
    else if (w.ended) note = w.ended;
    else if (w.over && w.over.reason === "aborted") note = "This game was stopped before it ended.";
    cls(d.note, "note" + (w.error || w.haltErr ? " err" : ""));
    put(d.note, note);
    d.note.title = note;
    const can = w.live && w.stoppable;
    cls(d.halt, "wbtn halt" + (can ? "" : " gone") + (w.halt === "armed" ? " armed" : ""));
    d.halt.disabled = !can || w.halt === "sent";
    put(d.halt, w.halt === "sent" ? "Stopping..." : w.halt === "armed" ? "Click again to stop" : "Stop the game");
    d.halt.title = "Ends the game and shuts down both players";
  }

  function restoreScroll(w) {
    if (!w.parts) return;
    const s = w.parts.seat;
    if (Math.abs(s.steps.scrollTop - s.scroll) > 1) s.steps.scrollTop = s.scroll;
  }

  function refresh() {
    const w = APP.watch;
    if (!w || !w.node || !APP.state) return;
    const beat = w.beat;
    const pend = w.live && w.pending && cursor() >= w.log.length ? w.pending : null;
    const pendKey = pend ? pend.seat + pend.type + ":" + Math.floor((Date.now() - pend.since) / 1000) : "";
    const key = [cursor(), beat ? beat.type + ":" + (beat.reason || "") + ":" + beat.entry.at : "-", w.paused, w.speed, w.follow, w.pace, pace(w), panelSeat(w), w.live, w.log.length, w.done, w.error,
      !!APP.state.over, pendKey, w.stoppable, w.halt, w.haltErr, w.ended].join("|");
    if (key === w.key) return;
    w.key = key;
    paintSeat(w, w.parts.seat);
    paintDesk(w, w.parts.desk);
    paintTally(w, w.parts.desk);
    if (typeof requestAnimationFrame !== "undefined") requestAnimationFrame(function () { restoreScroll(w); });
  }

  function mindsNode() {
    const w = APP.watch;
    if (!w.node) {
      const node = el("aside", null);
      node.id = "minds";
      node.setAttribute("aria-label", "What the planner and Jev are doing");
      const seat = buildSeat(), desk = buildDesk(w);
      node.appendChild(seat.box);
      node.appendChild(desk.desk);
      w.parts = { seat: seat, desk: desk };
      w.node = node;
      syncPace(w);
    }
    refresh();
    if (typeof requestAnimationFrame !== "undefined") requestAnimationFrame(function () { restoreScroll(w); });
    return w.node;
  }

  Object.assign(U, { watchBoot: watchBoot, watchStop: watchStop, watchTick: tick, mindsNode: mindsNode, renderWatchList: renderWatchList });
})();
